import {
  IBridgeDevice,
  BridgeDeviceStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  Firestore,
  IAttachment,
  WithRef,
} from '@principle-theorem/shared';
import { BridgeDevice } from './bridge-device';

export enum BridgeLogFreshness {
  None = 'none',
  Stale = 'stale',
  Fresh = 'fresh',
}

export interface IBridgeDeviceDiagnosticStatus {
  deviceRef: DocumentReference<IBridgeDevice>;
  deviceName: string;
  deviceId: string;
  status: BridgeDeviceStatus;
  deleted: boolean;
  downloadUrl?: string;
  version?: string;
  hasSettings: boolean;
  hasIntegrationKey: boolean;
  logCount: number;
  latestLogDate?: Date;
  logFreshness: BridgeLogFreshness;
}

export async function getBridgeDeviceDiagnosticStatuses(
  devices: WithRef<IBridgeDevice>[]
): Promise<IBridgeDeviceDiagnosticStatus[]> {
  const nonDeletedDevices = devices.filter(
    (device) => !device.deleted
  );

  const statuses: IBridgeDeviceDiagnosticStatus[] = [];

  for (const device of nonDeletedDevices) {
    const files = await Firestore.getDocs(BridgeDevice.fileCol(device));
    const logFiles = files.filter((file) => file.type === 'log');
    const latestLog = findLatestLog(logFiles);
    const latestLogDate = latestLog
      ? new Date(latestLog.lastModified)
      : undefined;

    statuses.push({
      deviceRef: device.ref,
      deviceName: device.name,
      deviceId: device.deviceId ?? '',
      status: device.status,
      deleted: device.deleted,
      version: device.settings?.version,
      hasSettings: !!device.settings,
      hasIntegrationKey: !!device.integrationKey,
      logCount: logFiles.length,
      latestLogDate,
      logFreshness: getLogFreshness(latestLogDate),
    });
  }

  return statuses;
}

function findLatestLog(
  logFiles: WithRef<IAttachment>[]
): WithRef<IAttachment> | undefined {
  return logFiles.reduce<WithRef<IAttachment> | undefined>(
    (latest, file) =>
      !latest || file.lastModified > latest.lastModified ? file : latest,
    undefined
  );
}

function getLogFreshness(latestLogDate?: Date): BridgeLogFreshness {
  if (!latestLogDate) {
    return BridgeLogFreshness.None;
  }

  const today = new Date();
  const isToday =
    latestLogDate.getFullYear() === today.getFullYear() &&
    latestLogDate.getMonth() === today.getMonth() &&
    latestLogDate.getDate() === today.getDate();

  return isToday ? BridgeLogFreshness.Fresh : BridgeLogFreshness.Stale;
}
